import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { Spin, Empty } from "antd";
import { LockOutlined, PictureOutlined, FileImageOutlined } from "@ant-design/icons";

import { Post, getPostsBySubcategory, getOcspstLinkedPosts, getPostData, getImageList, getGrossImageList, isPostDetailBlocked } from "../api/postAPI";
import { getCategoryInfo } from "../api/categoryAPI";
import { OCSPST_CATEGORY } from "../component/category/ocspstCategory";
import { MainColor, SubColor } from "../style/styleValue";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 10;
const photoUrl = (path: string) => `${BACKEND}/api/photo/${path}`;
const grossUrl = (path: string) => `${BACKEND}/api/gross-photo/file/${path}`;

// 균일 썸네일 타일 크기 및 masonry 그리드 상수
const TILE = 150; // 썸네일 한 변(px) — 모든 썸네일 동일 크기
const GAP = 14; // 타일/블록 간격(px)
const ROW_UNIT = 10; // masonry 세로 기준 단위(px)
const MAX_COLS = 4; // 한 블록의 최대 가로 타일 수
const PAD = 14; // 블록 상하 여백(px)
const META_H = 46; // 메타(제목+작성자) 예상 높이(px)
const META_GAP = 10; // 메타와 썸네일 사이 간격(px)
const BUFFER = 14; // 높이 추정 안전 여유(px)

// 썸네일 개수에 따른 블록 가로 타일 수
const pickCols = (count: number) => Math.min(count, MAX_COLS);

// masonry 세로 span 계산 (블록 예상 높이를 ROW_UNIT 단위로 환산)
const computeRowSpan = (count: number) => {
  const cols = pickCols(count);
  const rows = Math.ceil(count / cols);
  const blockHeight = PAD * 2 + META_H + META_GAP + rows * TILE + (rows - 1) * GAP + BUFFER;
  return Math.ceil((blockHeight + GAP) / (ROW_UNIT + GAP));
};

type MediaKind = "slide" | "image" | "gross";

interface MediaThumb {
  key: string;
  url: string;
  kind: MediaKind;
  label?: string;
}

interface PostMedia {
  thumbs: MediaThumb[];
  slideCount: number;
  imageCount: number;
  loading: boolean;
  locked: boolean;
}

const emptyMedia: PostMedia = { thumbs: [], slideCount: 0, imageCount: 0, loading: true, locked: false };

const GalleryMosaic = () => {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  // /post-list/ocspst : 실제 카테고리가 아닌 OCSPST 링크 게시글 모음
  const isOcspst = categoryId === OCSPST_CATEGORY.key;

  const [posts, setPosts] = useState<Post[]>([]);
  const [mediaByPost, setMediaByPost] = useState<Record<number, PostMedia>>({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  // 카테고리명/슬라이드 수는 getCategoryInfo 로 조회 (헤더 title·count 용) — searchParams 불필요
  const [categoryInfo, setCategoryInfo] = useState<{ name: string; slideCnt: number } | null>(null);

  const pageRef = useRef(1);
  const fetchingRef = useRef(false);
  const cancelledRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const organName = categoryInfo?.name || "갤러리";

  const loadNextPage = useCallback(async () => {
    if (!categoryId || fetchingRef.current || !hasMore) return;
    fetchingRef.current = true;

    const page = pageRef.current;
    if (page === 1) setInitialLoading(true);
    else setLoadingMore(true);

    try {
      const res: Post[] = isOcspst
        ? await getOcspstLinkedPosts(page, PAGE_SIZE, "modifiedAt", "DESC")
        : await getPostsBySubcategory(
            page,
            PAGE_SIZE,
            +categoryId,
            "modifiedAt",
            "DESC",
            null,
            null
          );
      if (cancelledRef.current) return;

      const batch = Array.isArray(res) ? res : [];
      setPosts((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...batch.filter((p) => !seen.has(p.id))];
      });
      setMediaByPost((prev) => ({
        ...prev,
        ...Object.fromEntries(batch.map((p) => [p.id, { ...emptyMedia }])),
      }));

      if (batch.length < PAGE_SIZE) setHasMore(false);
      pageRef.current = page + 1;

      batch.forEach(async (post) => {
        const media = await fetchPostMedia(post);
        if (!cancelledRef.current) {
          setMediaByPost((prev) => ({ ...prev, [post.id]: media }));
        }
      });
    } catch (e) {
      console.error("GalleryMosaic load error:", e);
      if (!cancelledRef.current) setHasMore(false);
    } finally {
      if (!cancelledRef.current) {
        setInitialLoading(false);
        setLoadingMore(false);
      }
      fetchingRef.current = false;
    }
  }, [categoryId, isOcspst, hasMore]);

  useEffect(() => {
    cancelledRef.current = false;
    pageRef.current = 1;
    setPosts([]);
    setMediaByPost({});
    setHasMore(true);
    setInitialLoading(true);
    loadNextPage();

    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  // 헤더 title·count 용 카테고리 정보(name, slideCnt) 조회
  useEffect(() => {
    if (!categoryId) return;
    let cancelled = false;
    setCategoryInfo(null);
    if (isOcspst) {
      // 가상 카테고리라 카테고리 정보가 없다. 슬라이드 수는 링크 게시글 전체에서 합산한다.
      getOcspstLinkedPosts(1, 9999, "modifiedAt", "DESC").then((all) => {
        if (!cancelled) {
          setCategoryInfo({
            name: OCSPST_CATEGORY.name,
            slideCnt: all.reduce((sum, p: any) => sum + (p.slideCnt ?? 0), 0),
          });
        }
      });
      return () => {
        cancelled = true;
      };
    }
    getCategoryInfo(+categoryId)
      .then((info) => {
        if (!cancelled) setCategoryInfo({ name: info.name, slideCnt: info.slideCnt });
      })
      .catch((e) => console.error("GalleryMosaic category info load error:", e));
    return () => {
      cancelled = true;
    };
  }, [categoryId, isOcspst]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadNextPage();
      },
      { rootMargin: "300px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadNextPage, initialLoading, posts.length, hasMore]);

  const goToPost = (post: Post) => {
    const params = new URLSearchParams();
    params.set("title", post.title);
    params.set("from", "gallery");
    if (organName !== "갤러리") params.set("name", organName);
    if (categoryId) params.set("categoryId", categoryId);
    navigate(`/post/${post.id}?${params.toString()}`);
  };

  // 미디어가 하나라도 있는 게시글만 노출
  const visiblePosts = posts.filter((post) => {
    const m = mediaByPost[post.id];
    return !!m && !m.loading && m.thumbs.length > 0;
  });
  const anyPending = posts.some((post) => {
    const m = mediaByPost[post.id];
    return !m || m.loading;
  });

  if (initialLoading) {
    return (
      <CenterState>
        <Spin size="large" />
      </CenterState>
    );
  }

  return (
    <Container>
      <PageHeader>
        <Eyebrow>ATLAS GALLERY</Eyebrow>
        <Title>{organName}</Title>
        <Count>{categoryInfo?.slideCnt ?? 0} slides</Count>
      </PageHeader>

      {visiblePosts.length === 0 ? (
        anyPending || loadingMore || hasMore ? (
          <CenterState>
            <Spin size="large" />
          </CenterState>
        ) : (
          <CenterState>
            <Empty description="표시할 갤러리가 없습니다." />
          </CenterState>
        )
      ) : (
        // masonry 그리드: 열 단위는 고정 타일 폭(TILE). 블록은 썸네일 개수에 따라 1~MAX_COLS 열을
        // 차지하고, grid-auto-flow: dense 로 빈자리를 작은 블록들이 틈틈이 메우며 쭉 이어진다.
        // 썸네일은 모두 동일한 TILE 크기(정사각, object-fit: cover)라 늘어나거나 깨지지 않는다.
        <MasonryGrid>
          {visiblePosts.map((post) => {
            const media = mediaByPost[post.id] ?? emptyMedia;
            const count = media.thumbs.length;
            const cols = pickCols(count);
            return (
              <Block
                key={post.id}
                onClick={() => goToPost(post)}
                title={post.title || "제목 없음"}
                style={{ gridColumn: `span ${cols}`, gridRow: `span ${computeRowSpan(count)}` }}
              >
                <Meta>
                  <MTitle title={post.title || "제목 없음"}>
                    {post.isSecret && <LockOutlined style={{ marginRight: 5, color: "#8c8c8c" }} />}
                    {post.title || "제목 없음"}
                  </MTitle>
                  <MSub>
                    <MWriter>{post.writer || "작성자 미상"}</MWriter>
                    <MCounts>
                      {media.slideCount > 0 && (
                        <span>
                          <FileImageOutlined /> {media.slideCount}
                        </span>
                      )}
                      {media.imageCount > 0 && (
                        <span>
                          <PictureOutlined /> {media.imageCount}
                        </span>
                      )}
                    </MCounts>
                  </MSub>
                </Meta>

                <Thumbs style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
                  {media.thumbs.map((t) => (
                    <Tile key={t.key} title={t.label}>
                      {t.label && (
                        <KindTag $kind={t.kind} title={t.label}>
                          {t.label}
                        </KindTag>
                      )}
                      <img
                        src={t.url}
                        alt={t.label || ""}
                        loading="lazy"
                        onError={(e) => {
                          const el = e.currentTarget;
                          el.style.display = "none";
                          (el.parentElement as HTMLElement).classList.add("broken");
                        }}
                      />
                    </Tile>
                  ))}
                </Thumbs>
              </Block>
            );
          })}
        </MasonryGrid>
      )}

      {posts.length > 0 && (
        <ScrollFooter ref={sentinelRef}>
          {loadingMore && <Spin />}
          {/* {!hasMore && !loadingMore && <EndText>모든 게시글을 불러왔습니다.</EndText>} */}
        </ScrollFooter>
      )}
    </Container>
  );
};

const fetchPostMedia = async (post: Post): Promise<PostMedia> => {
  if (post.isSecret) {
    return { thumbs: [], slideCount: 0, imageCount: 0, loading: false, locked: true };
  }

  try {
    const [detail, images, grossImages] = await Promise.all([
      getPostData(post.id),
      getImageList(post.id),
      getGrossImageList(post.id),
    ]);

    if (isPostDetailBlocked(detail)) {
      return { thumbs: [], slideCount: 0, imageCount: 0, loading: false, locked: true };
    }

    const slides: any[] = detail?.slides ?? post.slides ?? [];
    const photos: any[] = Array.isArray(images) ? images : [];
    const gross: any[] = Array.isArray(grossImages) ? grossImages : [];

    const thumbs: MediaThumb[] = [
      ...slides
        .filter((s) => s?.thumbnail)
        .map((s, i) => ({ key: `s-${s.slideId ?? i}`, url: photoUrl(s.thumbnail), kind: "slide" as const, label: s.slidename })),
      ...photos
        .filter((p) => p?.pathFileName)
        .map((p, i) => ({ key: `i-${p.photoId ?? i}`, url: photoUrl(p.pathFileName), kind: "image" as const, label: p.imageName })),
      ...gross
        .filter((g) => g?.pathFileName)
        .map((g, i) => ({ key: `g-${g.grossPhotoId ?? i}`, url: grossUrl(g.pathFileName), kind: "gross" as const, label: g.caption })),
    ];

    return {
      thumbs,
      slideCount: slides.length,
      imageCount: photos.length + gross.length,
      loading: false,
      locked: false,
    };
  } catch (e) {
    console.error(`post ${post.id} media load error:`, e);
    return { thumbs: [], slideCount: 0, imageCount: 0, loading: false, locked: false };
  }
};

const Container = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 2.5rem 4rem 5rem;

  @media (max-width: 768px) {
    padding: 1.5rem 1.25rem 3rem;
  }
`;

const CenterState = styled.div`
  min-height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding-bottom: 2rem;
  margin-bottom: 2.5rem;
  border-bottom: 1px solid #eef1f5;
`;

const Eyebrow = styled.span`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  color: ${SubColor};
  margin-bottom: 10px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 2.4rem;
  font-weight: 700;
  color: ${MainColor};
  letter-spacing: -0.5px;
`;

const Count = styled.span`
  margin-top: 10px;
  font-size: 0.95rem;
  color: #999;
`;

const MasonryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, ${TILE}px);
  grid-auto-rows: ${ROW_UNIT}px;
  gap: ${GAP}px;
  grid-auto-flow: row dense;
  justify-content: center;
`;

const Block = styled.section`
  box-sizing: border-box;
  overflow: hidden;
  padding: ${PAD}px 0;
  background: #fff;
  border-radius: 12px;
  /* box-shadow 로 테두리를 그려 레이아웃(타일 폭)에 영향 주지 않음 */
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.05), 0 0 0 1px #eef1f5;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.1), 0 0 0 1px #dbe2ea;
    transform: translateY(-2px);
  }
`;

const Meta = styled.div`
  padding: 0 ${PAD}px;
  margin-bottom: ${META_GAP}px;
  min-width: 0;
`;

const MTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 0.95rem;
  font-weight: 700;
  color: #222;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${Block}:hover & {
    color: ${MainColor};
  }
`;

const MSub = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const MWriter = styled.span`
  font-size: 0.78rem;
  color: #8a94a3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MCounts = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  color: #667085;
  font-size: 0.72rem;

  span {
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }
  .anticon {
    font-size: 11px;
  }
`;

const Thumbs = styled.div`
  display: grid;
  grid-auto-rows: ${TILE}px;
  gap: ${GAP}px;
`;

const Tile = styled.div`
  position: relative;
  height: ${TILE}px;
  overflow: hidden;
  background: #f0f2f5;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.35s ease;
  }

  ${Block}:hover & img {
    transform: scale(1.04);
  }

  &.broken::after {
    content: "";
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(45deg, #f0f2f5, #f0f2f5 10px, #e8ebf0 10px, #e8ebf0 20px);
  }
`;

const KindTag = styled.span<{ $kind: MediaKind }>`
  position: absolute;
  top: 6px;
  left: 6px;
  right: 6px;
  z-index: 1;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #fff;
  padding: 2px 6px;
  border-radius: 4px;
  width: fit-content;
  max-width: calc(100% - 12px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: ${({ $kind }) =>
    $kind === "slide"
      ? "rgba(23, 55, 95, 0.85)"
      : $kind === "gross"
      ? "rgba(180, 83, 9, 0.85)"
      : "rgba(48, 104, 164, 0.85)"};
`;

const ScrollFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60px;
  padding: 2rem 0 0;
`;

const EndText = styled.span`
  color: #b5bcc7;
  font-size: 0.9rem;
`;

export default GalleryMosaic;
